import { useState } from 'react'

import { usePollComponentStatus } from '../hooks/usePollComponentStatus'
import { useRestartGateway } from '../hooks/useRestartGateway'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Modal } from '@hivewatch/dark'

import {
  ControlledMultiSelectInput,
  ControlledSelect,
  ControlledTextInput,
  SkeletonModalBody,
} from '@/components/ui'
import { OptionI } from '@/utils/forms/optionBuilders'

import { DarkGatewayComponentStatus } from '../gateway-status'
import {
  ALLOWED_RESTART_COMPONENT_OPTIONS,
  RestartGatewayFormInputsIProps,
} from '../utils/utils'
import { DarkConfirmGatewayRestartDialog } from './DarkConfirmGatewayRestartDialog'

interface DarkGatewayDetailsModalIProps {
  id: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link GatewayDetailsModal} built on `@hivewatch/dark`.
 * Same read-only name/devices fields and restart-component flow — the chrome,
 * fields, status table and confirm dialog move to the design system. Rendered
 * from the dark gateways table (`DarkGatewaysTableModal`); the legacy table
 * keeps the legacy modal. Without the restart permission the footer is
 * hidden entirely — the modal closes via its own close affordance.
 */
export const DarkGatewayDetailsModal = ({
  id,
  isOpen,
  onClose,
}: DarkGatewayDetailsModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableRestartGateway } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const { deviceData, devices, isDeviceLoading, restart, isRestartLoading } =
    useRestartGateway(id)
  const gatewayName = deviceData?.edgeDevice?.name ?? ''

  const { handleSubmit, control, errors } =
    useForm<RestartGatewayFormInputsIProps>({
      reValidateMode: 'onChange',
    })
  const selectedComponent: OptionI | null = useWatch({
    control,
    name: 'component',
    defaultValue: null,
  })

  const {
    componentStatusData,
    componentStatusError,
    isComponentStatusLoading,
  } = usePollComponentStatus({
    shouldSkip: !selectedComponent,
    componentName: selectedComponent?.value ?? '',
    edgeDeviceName: gatewayName,
  })

  const onSubmit: SubmitHandler<RestartGatewayFormInputsIProps> = async (
    values
  ) => {
    try {
      await restart(gatewayName, values.component.value)
      showSuccess(t('gateways:crud_restart_success'))
    } catch (error) {
      showError(error)
    } finally {
      setIsConfirmOpen(false)
    }
  }

  // Gateway still loading — render the skeleton with no footer actions, so
  // the fields below can seed their defaultValues from loaded data.
  if (isDeviceLoading) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW='600px'
        onClose={onClose}
        size='compact'
        title={t('gateways:crud_details_title')}
      >
        <SkeletonModalBody />
      </Modal>
    )
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={!selectedComponent}
      isSaveHidden={!shouldEnableRestartGateway}
      isSaveLoading={isRestartLoading}
      maxW='600px'
      onClose={onClose}
      onSave={() => setIsConfirmOpen(true)}
      saveButtonLabel={t('gateways:crud_button_restart')}
      showCancelButton
      size='compact'
      title={t('gateways:crud_details_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='GatewayDetailsModal:name'
          defaultValue={gatewayName}
          disabled
          errors={errors}
          label={t('gateways:crud_field_name_label')}
          name='name'
          width='100%'
        />

        <ControlledMultiSelectInput
          colorScheme={colorScheme}
          control={control}
          data-testid='GatewayDetailsModal:devices'
          defaultValue={devices}
          disabled
          label={t('gateways:crud_field_devices_label')}
          name='devices'
          options={devices}
          placeholder={t('gateways:crud_field_devices_empty_placeholder')}
          searchable
        />

        {shouldEnableRestartGateway && (
          <>
            <ControlledSelect
              colorScheme={colorScheme}
              control={control}
              data-testid='GatewayDetailsModal:component'
              isSearchable
              label={t('gateways:crud_field_component_label')}
              name='component'
              options={ALLOWED_RESTART_COMPONENT_OPTIONS}
              placeholder={t('gateways:crud_field_component_placeholder')}
            />
            {selectedComponent && (
              <Box>
                <DarkGatewayComponentStatus
                  componentStatusData={componentStatusData}
                  hasError={componentStatusError !== undefined}
                  isLoading={isComponentStatusLoading}
                />
              </Box>
            )}
          </>
        )}
      </Flex>
      {isConfirmOpen && (
        <DarkConfirmGatewayRestartDialog
          componentName={selectedComponent?.value}
          gatewayName={gatewayName}
          isOpen={isConfirmOpen}
          isRestartLoading={isRestartLoading}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleSubmit(onSubmit)}
        />
      )}
    </Modal>
  )
}
